<script lang="ts">
	import { beforeUpdate } from 'svelte';
	// Stores
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { getDeviceState } from 'src/stores/device-runtime/states';
	// Services and configs
	import { devices_config } from 'src/configs/devices';

	// Helpers
	import {
		get_gen_2_component_channel_structure,
		get_mode,
		is_gen_2,
		isXMODDevice,
		real_id,
	} from 'src/helpers/interface_helpers';
	import { status_configurator } from 'src/helpers/device_data_helpers';
	import { isPeripheralDevice } from 'src/helpers/addons/addon_device_helpers';

	// Design System Components
	import { ControlsContainer } from '@Shelly/shelly-design-system';

	// Components
	import ControlOnOff from 'src/components/Controls/Single/ControlOnOff.svelte';
	import ControlSlider from 'src/components/Controls/Single/ControlSlider.svelte';
	import InputsReaderVisualizer from 'src/components/Controls/Indicators/InputsReaderVisualizer.svelte';
	import TimerRemaining from 'src/components/TimerRemaining/TimerRemaining.svelte';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import DeviceViewStateWrapper from 'src/components/Sidepanel/DeviceViews/DeviceViewStateWrapper.svelte';
	import DeviceLabelList from 'src/components/Sidepanel/DeviceViews/DeviceLabelList.svelte';

	// Types
	import type { Device } from 'src/types/Device.ts';

	export let device: Device;

	const deviceConfig = devices_config?.[device?.type];
	const isGen2 = is_gen_2(device);

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;

	// Status and label processing
	$: _statusesRaw =
		status?.online && deviceConfig ? status_configurator(status, device, settings) : null;
	$: _statuses = (Array.isArray(_statusesRaw) ? _statusesRaw : []) as any[];
	$: labelList = _statuses.filter((e) => {
		return 'undefined' === typeof e.main && e.name !== 'state';
	});
	$: combinedLabelList = [...Object.values(timerLabels), ...labelList];

	let timerLabels: Record<string, any> = {};
	let gen_1_timer = false;
	let gen_2_timer = false;
	let deviceState: any = getDeviceState(device?.id);

	// Mode detection for XMOD devices
	$: mode = device?.id
		? get_mode(device, $statusStore, $settingsStore)
		: null;

	// XMOD device specific state (matching DeviceDrawer beforeUpdate logic)
	$: hasColorControl = isXMODDevice(device?.type)
		? status?.['rgbw:0'] && device?.mode === 'rgbw'
		: deviceConfig?.modes?.[mode]?.color;
	$: hasColorTempControl = isXMODDevice(device?.type)
		? status?.['cct:0'] && device?.mode === 'cct'
		: deviceConfig?.modes?.[mode]?.color_temp;
	$: hasLightControl = isXMODDevice(device?.type) && status?.['light:0'] && device?.mode === 'light';
	$: sliderProp = (() => {
		if (isXMODDevice(device?.type)) {
			return hasColorControl || hasColorTempControl || hasLightControl ? 'brightness' : '';
		}
		return deviceConfig?.modes?.[mode]?.slider || '';
	})();

	// Check if LabelList should be shown (based on DeviceDrawer line 1372)
	$: shouldShowLabelList =
		!isPeripheralDevice(device?.type) &&
		device?.mode !== 'media_player' &&
		device?.category !== 'emeter' &&
		device?.category !== 'pv';

	beforeUpdate(() => {
		if (status?.online && deviceConfig) {
			// Gen 1 timer check
			if (!isGen2 && device?.category !== 'emeter') {
				gen_1_timer = status?.[device?.category + 's']?.[device?.channel]?.has_timer || false;
			}

			// Gen 2 timer check
			if (isGen2) {
				const component = get_gen_2_component_channel_structure(device);
				gen_2_timer =
					typeof status?.[component]?.timer_started_at !== 'undefined' &&
					((settings?.[component]?.auto_on && !status?.[component]?.output) ||
						(settings?.[component]?.auto_off && status?.[component]?.output));
			}
		}
		deviceState = getDeviceState(device?.id);
	});
</script>

<DeviceViewStateWrapper {device}>
	<div
		slot="staticTop"
		class="label-list-wrapper mb-s d-flex flex-direction-column justify-content-center"
	>
		{#if device && status !== undefined && settings !== undefined && deviceConfig !== undefined && status?.online && shouldShowLabelList}
			{#if gen_2_timer}
				{#key status[get_gen_2_component_channel_structure(device)].timer_started_at}
					<TimerRemaining timerKey="gen2_main" {device} {status} {settings} bind:timerLabels />
				{/key}
			{:else if gen_1_timer && status?.[`${device.category}s`]?.[device.channel]?.timer_started}
				{#key status[`${device.category}s`][device.channel].timer_started}
					<TimerRemaining timerKey="gen1_main" {device} {status} {settings} bind:timerLabels />
				{/key}
			{/if}
			<DeviceLabelList labelList={combinedLabelList} />
		{/if}
		<DeviceNotificationLabel deviceId={device?.id} />
	</div>

	<!-- XMOD Device controls - handles different modes -->
	<div class="flex-full-height justify-content-top" slot="content">
		{#if isXMODDevice(device?.type) && mode === 'inputs_reader'}
			<ControlsContainer color="color-0">
				<InputsReaderVisualizer
					slot="first"
					{device}
					{status}
					{settings}
					isWide
					isInCard={false}
				/>
			</ControlsContainer>
		{:else if isXMODDevice(device?.type) && ['light', 'cct', 'rgbw'].includes(device?.mode) && sliderProp}
			<!-- Show brightness slider for light/cct/rgbw modes -->
			<ControlSlider {device} {status} {settings} prop={sliderProp} type="vertical" />
		{/if}
	</div>

	<!-- staticBottom content -->
	<div class="flex-full-height justify-content-end mt-s" slot="staticBottom">
		{#if isXMODDevice(device?.type) && mode === 'inputs_reader'}
			<!-- Inputs reader mode doesn't show ControlOnOff -->
		{:else if isXMODDevice(device?.type) && (mode === 'relay' || mode === 'switch')}
			<ControlOnOff {device} {status} {settings} isWide useV2Components={true} />
		{:else if isXMODDevice(device?.type) && ['light', 'cct', 'rgbw'].includes(device?.mode)}
			<ControlOnOff {device} {status} {settings} isWide useV2Components={true} />
		{:else if isXMODDevice(device?.type) && (status?.['switch:0'] || status?.['light:0'] || status?.['cct:0'] || status?.['rgbw:0'])}
			<ControlOnOff {device} {status} {settings} isWide useV2Components={true} />
		{/if}
	</div>
</DeviceViewStateWrapper>
